import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { IngredientIcon } from '@/components/ui/ingredient-icon';
import { Camera, Upload, Trash2, MoveHorizontal, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import { useCreateMenuItem } from '@/hooks/useMenuItems';
import { useCreateMenuCategory } from '@/hooks/useMenuCategories';
import { useKitchenSpecialties } from '@/hooks/useKitchenSpecialties';
import { useIngredients } from '@/hooks/useIngredients';
import { useSetMenuItemSpecialties } from '@/hooks/useKitchenSpecialties';
import { useInventory } from '@/hooks/useInventory';
import { toast } from '@/hooks/use-toast';
import { getCompatibleUnits } from '@/lib/unit-utils';
import type { MenuItemIngredientInput, MeasurementUnit } from '@/types/api.types';

interface OCRSpaceResponse {
  ParsedResults?: Array<{
    ParsedText: string;
    ErrorMessage?: string;
    ErrorDetails?: string;
  }>;
  IsErroredOnProcessing?: boolean;
  ErrorMessage?: string | string[];
  ErrorDetails?: string;
}

interface ExtractedCategory {
  id: string;
  nameAr: string;
  nameFr: string;
  selected: boolean;
}

interface ExtractedMenuItem {
  id: string;
  categoryId: string;
  nameAr: string;
  nameFr: string;
  descriptionAr: string;
  descriptionFr: string;
  price: number;
  selected: boolean;
  specialtyIds: string[];
  ingredients: MenuItemIngredientInput[];
}

interface MenuOCRImportProps {
  restaurantId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MenuOCRImport({ restaurantId, open, onOpenChange }: MenuOCRImportProps) {
  const { t, i18n } = useTranslation();
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [extractedCategories, setExtractedCategories] = useState<ExtractedCategory[]>([]);
  const [extractedItems, setExtractedItems] = useState<ExtractedMenuItem[]>([]);
  const [currentStep, setCurrentStep] = useState<'upload' | 'processing' | 'review' | 'importing'>(
    'upload'
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const createCategory = useCreateMenuCategory(restaurantId);
  const createMenuItem = useCreateMenuItem();
  const { data: specialties } = useKitchenSpecialties(restaurantId);
  const { data: ingredients } = useIngredients(restaurantId);
  const { data: inventory } = useInventory(restaurantId);
  const setSpecialtiesMutation = useSetMenuItemSpecialties();

  // State for ingredient form (per item)
  const [ingredientForm, setIngredientForm] = useState<
    Record<
      string,
      {
        ingredientId: string;
        quantity: number;
        unit: MeasurementUnit;
      }
    >
  >({});

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const detectLanguage = (text: string): 'fr' | 'ar' => {
    // Simple detection: if text contains Arabic characters, it's Arabic
    const arabicPattern = /[\u0600-\u06FF]/;
    return arabicPattern.test(text) ? 'ar' : 'fr';
  };

  const separateMixedLanguages = (text: string): { ar: string; fr: string } => {
    // Separate Arabic and French text from mixed lines
    const arabicPattern = /[\u0600-\u06FF\s]+/g;
    const arabicMatches = text.match(arabicPattern);
    const arabicText = arabicMatches ? arabicMatches.join(' ').trim() : '';

    // Remove Arabic characters to get French text
    const frenchText = text
      .replace(/[\u0600-\u06FF]/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();

    return { ar: arabicText, fr: frenchText };
  };

  const extractPrice = (text: string): number | null => {
    // Match patterns like: 50 DH, 50DH, 50 00, DH 50, just numbers at end, etc.
    const pricePatterns = [
      /(\d+)\s*(?:DH|dh|MAD|mad|Dh)(?:\s+\d+)?/i, // Matches "40 DH" or "40DH 0"
      /(\d+)(?:[.,]\d{1,2})?\s*(?:DH|dh|MAD|mad|Dh)/i, // Matches "40.5 DH"
      /(?:DH|dh|MAD|mad|Dh)\s*(\d+(?:[.,]\d{1,2})?)/i, // Matches "DH 40"
      /\b(\d{2})\s*$/, // Just 2 digits at end of line (like "43")
      /\b(\d{2})\s+\d+\s*$/, // "43 0" or similar patterns
    ];

    for (const pattern of pricePatterns) {
      const match = text.match(pattern);
      if (match && match[1]) {
        const priceStr = match[1].replace(',', '.');
        const price = parseFloat(priceStr);
        if (!isNaN(price) && price > 0 && price < 1000) {
          return price;
        }
      }
    }
    return null;
  };

  const analyzeOCRResult = (ocrText: string) => {
    // Check if we have text data
    if (!ocrText || ocrText.trim().length === 0) {
      console.error('No OCR text found');
      toast({
        title: t('common.error'),
        description: t('menu.noItemsDetected'),
        variant: 'destructive',
      });
      return;
    }

    // Parse the text line by line
    let textLines = ocrText.split('\n').filter((line) => line.trim().length > 0);

    // Preprocessing: Try to merge lines where price is on next line
    // Example: "Espagnol" on one line, "43 DH" on next line
    const mergedLines: string[] = [];
    for (let i = 0; i < textLines.length; i++) {
      const currentLine = textLines[i]?.trim() ?? '';
      const nextLine = i < textLines.length - 1 ? (textLines[i + 1]?.trim() ?? '') : '';

      // Check if current line has no price but next line does
      const currentHasPrice = extractPrice(currentLine) !== null;
      const nextHasPrice = extractPrice(nextLine) !== null;

      // If current line has no price and is short (likely item name)
      // and next line has a price (and is also short, likely just price)
      if (!currentHasPrice && nextHasPrice && nextLine.length < 15) {
        // Merge them
        mergedLines.push(`${currentLine} ${nextLine}`);
        i++; // Skip next line since we merged it
      } else {
        mergedLines.push(currentLine);
      }
    }

    textLines = mergedLines;

    if (textLines.length === 0) {
      toast({
        title: t('common.error'),
        description: t('menu.noItemsDetected'),
        variant: 'destructive',
      });
      return;
    }

    // Extract categories and items
    const categories: ExtractedCategory[] = [];
    const items: ExtractedMenuItem[] = [];
    let currentCategory: ExtractedCategory | null = null;
    let currentItem: ExtractedMenuItem | null = null;

    textLines.forEach((text) => {
      const trimmedText = text?.trim() ?? '';
      if (!trimmedText || trimmedText.length < 2) return;

      // Remove common bullet points and special chars from start
      let cleanText = trimmedText.replace(/^[♦\*•\-\+◆◇▪▫●○\s]+/, '').trim();

      // Remove random single characters or very short artifacts
      cleanText = cleanText.replace(/\s+[a-zA-Z]\s+/g, ' ').trim(); // Remove single letters
      cleanText = cleanText.replace(/\s{2,}/g, ' '); // Normalize spaces

      if (cleanText.length < 2) return;

      const price = extractPrice(cleanText);
      const hasPrice = price !== null;

      // RULE 1: Line has price -> Menu Item
      if (hasPrice) {
        // Extract item name (remove price)
        const itemName = cleanText
          .replace(/\d+(?:[.,]\d{1,2})?\s*(?:DH|dh|MAD|mad)(?:\s+\d+)?/gi, '')
          .replace(/\s+\d+\s*$/, '')
          .replace(/\s+0+\s*$/, '')
          .replace(/\s+/g, ' ')
          .trim();

        if (itemName.length < 2) return;

        // Check if line has mixed languages
        const lang = detectLanguage(itemName);
        const hasMixedLanguages = /[\u0600-\u06FF]/.test(itemName) && /[a-zA-Z]/.test(itemName);

        let nameAr = '';
        let nameFr = '';

        if (hasMixedLanguages) {
          // Separate the languages
          const separated = separateMixedLanguages(itemName);
          nameAr = separated.ar;
          nameFr = separated.fr;
        } else {
          // Single language
          if (lang === 'ar') {
            nameAr = itemName;
          } else {
            nameFr = itemName;
          }
        }

        currentItem = {
          id: `item-${Date.now()}-${Math.random()}`,
          categoryId: currentCategory?.id || '',
          nameAr,
          nameFr,
          descriptionAr: '',
          descriptionFr: '',
          price: price ?? 0,
          selected: true,
          specialtyIds: [],
          ingredients: [],
        };
        items.push(currentItem);
      }
      // RULE 2: No price + Short line (< 35 chars) -> Category
      else if (cleanText.length < 35) {
        // Skip if it contains commas (likely a comma-separated list = description)
        if (cleanText.includes(',')) {
          if (currentItem) {
            const lang = detectLanguage(cleanText);
            const hasMixedLanguages =
              /[\u0600-\u06FF]/.test(cleanText) && /[a-zA-Z]/.test(cleanText);

            if (hasMixedLanguages) {
              const separated = separateMixedLanguages(cleanText);
              if (separated.ar) {
                const separator = currentItem.descriptionAr ? ', ' : '';
                currentItem.descriptionAr += separator + separated.ar;
              }
              if (separated.fr) {
                const separator = currentItem.descriptionFr ? ', ' : '';
                currentItem.descriptionFr += separator + separated.fr;
              }
            } else {
              const separator = currentItem.descriptionFr || currentItem.descriptionAr ? ', ' : '';
              if (lang === 'ar') {
                currentItem.descriptionAr += separator + cleanText;
              } else {
                currentItem.descriptionFr += separator + cleanText;
              }
            }
          }
          return;
        }

        // Skip very common description words
        const descriptionWords =
          /^(jus|yaourt|eau|salade|fruits|chaude|boisson|tomates|oeufs|pain|beurre|confiture|fromage|café|thé|lait|croissant|chocolat|orange|pomme|viande|poulet|légumes|sauce|frites)/i;
        if (cleanText.match(descriptionWords)) {
          // This is a description, not a category
          if (currentItem) {
            const lang = detectLanguage(cleanText);
            const hasMixedLanguages =
              /[\u0600-\u06FF]/.test(cleanText) && /[a-zA-Z]/.test(cleanText);

            if (hasMixedLanguages) {
              const separated = separateMixedLanguages(cleanText);
              if (separated.ar) {
                const separator = currentItem.descriptionAr ? ', ' : '';
                currentItem.descriptionAr += separator + separated.ar;
              }
              if (separated.fr) {
                const separator = currentItem.descriptionFr ? ', ' : '';
                currentItem.descriptionFr += separator + separated.fr;
              }
            } else {
              const separator = currentItem.descriptionFr || currentItem.descriptionAr ? ', ' : '';
              if (lang === 'ar') {
                currentItem.descriptionAr += separator + cleanText;
              } else {
                currentItem.descriptionFr += separator + cleanText;
              }
            }
          }
          return;
        }

        // Skip if too short to be a real category name (< 3 chars)
        if (cleanText.length < 3) {
          return;
        }

        // It's a category
        const lang = detectLanguage(cleanText);
        const hasMixedLanguages = /[\u0600-\u06FF]/.test(cleanText) && /[a-zA-Z]/.test(cleanText);

        let nameAr = '';
        let nameFr = '';

        if (hasMixedLanguages) {
          // Separate the languages
          const separated = separateMixedLanguages(cleanText);
          nameAr = separated.ar;
          nameFr = separated.fr;
        } else {
          // Single language
          if (lang === 'ar') {
            nameAr = cleanText;
          } else {
            nameFr = cleanText;
          }
        }

        const categoryId = `cat-${Date.now()}-${Math.random()}`;
        const category: ExtractedCategory = {
          id: categoryId,
          nameAr,
          nameFr,
          selected: true,
        };
        categories.push(category);
        currentCategory = category;
        currentItem = null;
      }
      // RULE 3: No price + Longer line (>= 35 chars) -> Description
      else {
        // Add to current item's description
        if (currentItem) {
          const lang = detectLanguage(cleanText);
          const hasMixedLanguages = /[\u0600-\u06FF]/.test(cleanText) && /[a-zA-Z]/.test(cleanText);

          if (hasMixedLanguages) {
            // Separate the languages
            const separated = separateMixedLanguages(cleanText);
            if (separated.ar) {
              const separator = currentItem.descriptionAr ? ', ' : '';
              currentItem.descriptionAr += separator + separated.ar;
            }
            if (separated.fr) {
              const separator = currentItem.descriptionFr ? ', ' : '';
              currentItem.descriptionFr += separator + separated.fr;
            }
          } else {
            const separator = currentItem.descriptionFr || currentItem.descriptionAr ? ', ' : '';
            if (lang === 'ar') {
              currentItem.descriptionAr += separator + cleanText;
            } else {
              currentItem.descriptionFr += separator + cleanText;
            }
          }
        }
      }
    });

    console.log('Extracted categories:', categories);
    console.log('Extracted items:', items);

    // Post-processing: If no category detected, create a default one
    if (categories.length === 0 && items.length > 0) {
      const defaultCategory: ExtractedCategory = {
        id: `cat-${Date.now()}`,
        nameAr: 'قائمة الطعام',
        nameFr: 'Menu',
        selected: true,
      };
      categories.push(defaultCategory);
      // Assign all items to this category
      items.forEach((item) => {
        if (!item.categoryId) {
          item.categoryId = defaultCategory.id;
        }
      });
    }

    // Post-processing: Clean up descriptions
    items.forEach((item) => {
      // Remove duplicate text in descriptions
      if (item.descriptionFr) {
        item.descriptionFr = item.descriptionFr
          .replace(/,\s*,/g, ',') // Remove duplicate commas
          .replace(/\s+/g, ' ') // Normalize spaces
          .trim();
      }
      if (item.descriptionAr) {
        item.descriptionAr = item.descriptionAr.replace(/,\s*,/g, ',').replace(/\s+/g, ' ').trim();
      }
    });

    if (categories.length === 0 && items.length === 0) {
      toast({
        title: t('common.error'),
        description: t('menu.noItemsDetected'),
        variant: 'destructive',
      });
      return;
    }

    setExtractedCategories(categories);
    setExtractedItems(items);
    // Auto-expand all categories when results are loaded
    setExpandedCategories(new Set(categories.map(c => c.id)));
  };

  const processImage = async () => {
    if (!image) return;

    setIsProcessing(true);
    setCurrentStep('processing');
    setProgress(10);

    try {
      // Prepare form data for OCR.space API
      const formData = new FormData();
      formData.append('file', image);
      // No language specified - OCR Engine 2 will auto-detect (supports 100+ languages)
      formData.append('isOverlayRequired', 'false');
      formData.append('detectOrientation', 'true');
      formData.append('scale', 'true');
      formData.append('OCREngine', '2'); // Use OCR Engine 2 (auto-detects Arabic, French, etc.)
      formData.append('isTable', 'true'); // Detect table structure (helps with prices in columns)
      formData.append('apikey', 'K88018053488957');

      setProgress(30);

      // Call OCR.space API
      // Using free tier endpoint (no API key required, but rate limited)
      // For production, get a free API key from https://ocr.space/ocrapi
      const response = await fetch('https://api.ocr.space/parse/image', {
        method: 'POST',
        body: formData,
      });

      setProgress(70);

      const result: OCRSpaceResponse = await response.json();

      console.log('OCR.space result:', result);

      // Check for errors
      if (result.IsErroredOnProcessing) {
        const errMsg =
          (Array.isArray(result.ErrorMessage) ? result.ErrorMessage[0] : result.ErrorMessage) ||
          result.ErrorDetails ||
          'OCR processing failed';
        throw new Error(errMsg);
      }

      if (!result.ParsedResults || result.ParsedResults.length === 0) {
        throw new Error('No text detected in image');
      }

      const parsedText = result.ParsedResults[0]?.ParsedText;

      if (result.ParsedResults[0]?.ErrorMessage) {
        throw new Error(result.ParsedResults[0]?.ErrorMessage);
      }

      // Log the raw OCR text for debugging
      console.log('Raw OCR text:');
      console.log('---START---');
      console.log(parsedText);
      console.log('---END---');

      setProgress(90);

      // Analyze the extracted text
      analyzeOCRResult(parsedText ?? '');

      setProgress(100);
      setCurrentStep('review');
      toast({
        title: t('common.success'),
        description: t('menu.ocrComplete'),
      });
    } catch (error: any) {
      console.error('OCR Error:', error);
      toast({
        title: t('common.error'),
        description: error instanceof Error ? error.message : t('error.generic'),
        variant: 'destructive',
      });
      setCurrentStep('upload');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    setCurrentStep('importing');
    setProgress(0);

    try {
      // Import selected categories first
      const selectedCategories = extractedCategories.filter((c) => c.selected);
      const categoryMap = new Map<string, string>(); // old ID -> new ID

      for (let i = 0; i < selectedCategories.length; i++) {
        const category = selectedCategories[i];
        if (!category) continue;
        try {
          // Use fallback: if one language is empty, use the other
          const nameAr = category.nameAr || category.nameFr;
          const nameFr = category.nameFr || category.nameAr;

          const newCategory = await createCategory.mutateAsync({
            nameAr: nameAr,
            nameFr: nameFr,
          });
          categoryMap.set(category.id, newCategory.id);
        } catch (error) {
          console.error('Failed to create category:', category, error);
        }
        setProgress(
          Math.round(((i + 1) / (selectedCategories.length + extractedItems.length)) * 100)
        );
      }

      // Import selected menu items
      const selectedItems = extractedItems.filter((item) => item.selected);
      let successCount = 0;

      for (let i = 0; i < selectedItems.length; i++) {
        const item = selectedItems[i];
        if (!item) continue;
        const newCategoryId = categoryMap.get(item.categoryId);

        if (!newCategoryId) {
          console.warn('Category not found for item:', item);
          continue;
        }

        try {
          // Use fallback: if one language is empty, use the other
          const nameAr = item.nameAr || item.nameFr || '';
          const nameFr = item.nameFr || item.nameAr || '';

          // Handle descriptions - only include if they have actual content
          const menuItemData: any = {
            restaurantId: restaurantId,
            categoryId: newCategoryId,
            nameAr,
            nameFr,
            price: item.price,
            isAvailable: true,
            ingredients: item.ingredients || [],
          };

          // Only add descriptions if they exist and are not empty
          if (item.descriptionAr && item.descriptionAr.trim()) {
            menuItemData.descriptionAr = item.descriptionAr;
          }
          if (item.descriptionFr && item.descriptionFr.trim()) {
            menuItemData.descriptionFr = item.descriptionFr;
          }

          const newMenuItemId = await createMenuItem.mutateAsync(menuItemData);

          // Set specialties if any are selected
          if (item.specialtyIds && item.specialtyIds.length > 0 && newMenuItemId) {
            try {
              await setSpecialtiesMutation.mutateAsync({
                menuItemId: newMenuItemId,
                specialtyIds: item.specialtyIds,
              });
            } catch (error) {
              console.error('Failed to set specialties:', error);
            }
          }

          successCount++;
        } catch (error) {
          console.error('Failed to create menu item:', item, error);
        }

        setProgress(
          Math.round(
            ((selectedCategories.length + i + 1) /
              (selectedCategories.length + selectedItems.length)) *
              100
          )
        );
      }

      toast({
        title: t('common.success'),
        description: t('menu.importSuccess', {
          count: successCount,
          total: selectedItems.length,
        }),
      });

      // Reset and close
      handleClose();
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: t('common.error'),
        description: t('error.generic'),
        variant: 'destructive',
      });
    }
  };

  const handleClose = () => {
    setImage(null);
    setImagePreview(null);
    setExtractedCategories([]);
    setExtractedItems([]);
    setCurrentStep('upload');
    setProgress(0);
    onOpenChange(false);
  };

  const toggleCategory = (categoryId: string) => {
    setExtractedCategories((prev) =>
      prev.map((c) => (c.id === categoryId ? { ...c, selected: !c.selected } : c))
    );
    // Also update items in this category
    setExtractedItems((prev) =>
      prev.map((item) => {
        const cat = extractedCategories.find((c) => c.id === categoryId);
        // If cat is undefined, keep item.selected
        return item.categoryId === categoryId
          ? { ...item, selected: cat ? !cat.selected : item.selected }
          : item;
      })
    );
  };

  const toggleItem = (itemId: string) => {
    setExtractedItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, selected: !item.selected } : item))
    );
  };

  const updateCategory = (categoryId: string, field: 'nameAr' | 'nameFr', value: string) => {
    setExtractedCategories((prev) =>
      prev.map((c) => (c.id === categoryId ? { ...c, [field]: value } : c))
    );
  };

  const updateItem = (
    itemId: string,
    field:
      | 'nameAr'
      | 'nameFr'
      | 'descriptionAr'
      | 'descriptionFr'
      | 'price'
      | 'categoryId'
      | 'specialtyIds'
      | 'ingredients',
    value: string | number | string[] | MenuItemIngredientInput[]
  ) => {
    setExtractedItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, [field]: value } : item))
    );
  };

  const moveItemToCategory = (itemId: string, newCategoryId: string) => {
    setExtractedItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, categoryId: newCategoryId } : item))
    );
  };

  const addNewCategory = () => {
    const newCategory: ExtractedCategory = {
      id: `cat-${Date.now()}-${Math.random()}`,
      nameAr: '',
      nameFr: '',
      selected: true,
    };
    setExtractedCategories((prev) => [...prev, newCategory]);
    // Auto-expand newly added category
    setExpandedCategories((prev) => new Set([...prev, newCategory.id]));
  };

  const addNewItemToCategory = (categoryId: string) => {
    const newItem: ExtractedMenuItem = {
      id: `item-${Date.now()}-${Math.random()}`,
      categoryId: categoryId,
      nameAr: '',
      nameFr: '',
      descriptionAr: '',
      descriptionFr: '',
      price: 0,
      selected: true,
      specialtyIds: [],
      ingredients: [],
    };
    setExtractedItems((prev) => [...prev, newItem]);
  };

  const deleteCategory = (categoryId: string) => {
    setExtractedCategories((prev) => prev.filter((c) => c.id !== categoryId));
    // Also delete all items in this category
    setExtractedItems((prev) => prev.filter((item) => item.categoryId !== categoryId));
  };

  const deleteItem = (itemId: string) => {
    setExtractedItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] sm:min-w-[80vw] sm:max-w-[95vw] h-[90vh] overflow-hidden flex flex-col gap-4 p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>{t('menu.ocrImport')}</DialogTitle>
          <DialogDescription>{t('menu.ocrImportDescription')}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2">
          {/* Upload Step */}
          {currentStep === 'upload' && (
            <div className="space-y-4 p-4">
              <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8">
                <div className="flex flex-col items-center gap-4">
                  {imagePreview ? (
                    <>
                      <div className="relative w-full">
                        <img
                          src={imagePreview}
                          alt="Menu preview"
                          className="max-h-96 mx-auto rounded-lg"
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2"
                          onClick={() => {
                            setImage(null);
                            setImagePreview(null);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="text-xs text-muted-foreground bg-muted p-3 rounded-md space-y-1">
                        <p className="font-semibold">{t('menu.ocrTips')}:</p>
                        <ul className="list-disc list-inside space-y-1 ml-2">
                          <li>{t('menu.ocrTip1')}</li>
                          <li>{t('menu.ocrTip2')}</li>
                        </ul>
                      </div>
                    </>
                  ) : (
                    <>
                      <Camera className="w-16 h-16 text-muted-foreground" />
                      <div className="text-center">
                        <p className="text-lg font-medium">{t('menu.uploadMenuImage')}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {t('menu.uploadMenuImageDesc')}
                        </p>
                      </div>
                    </>
                  )}

                  <Label htmlFor="menu-image" className="cursor-pointer">
                    <div className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90">
                      <Upload className="w-4 h-4" />
                      <span>{imagePreview ? t('menu.changeImage') : t('menu.selectImage')}</span>
                    </div>
                    <Input
                      id="menu-image"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageChange}
                    />
                  </Label>
                </div>
              </div>
            </div>
          )}

          {/* Processing Step */}
          {currentStep === 'processing' && (
            <div className="space-y-4 p-4">
              <div className="text-center space-y-4">
                <div className="inline-block p-4 rounded-full bg-primary/10">
                  <Camera className="w-12 h-12 text-primary animate-pulse" />
                </div>
                <div>
                  <p className="text-lg font-medium">{t('menu.processingImage')}</p>
                  <p className="text-sm text-muted-foreground">{t('menu.pleaseWait')}</p>
                </div>
                <Progress value={progress} className="w-full max-w-md mx-auto" />
                <p className="text-sm text-muted-foreground">{progress}%</p>
              </div>
            </div>
          )}

          {/* Review Step */}
          {currentStep === 'review' && (
            <div className="space-y-4 p-2 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-4 border-b">
                <h3 className="text-lg font-semibold">{t('menu.reviewExtracted')}</h3>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <Button variant="outline" size="sm" onClick={addNewCategory} className="w-full sm:w-auto">
                    <Plus className="w-4 h-4 me-2" />
                    <span className="truncate">{t('menu.addCategory')}</span>
                  </Button>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        const allCategoryIds = new Set(extractedCategories.map(c => c.id));
                        setExpandedCategories(allCategoryIds);
                      }}
                      className="text-xs flex-1 sm:flex-none"
                    >
                      {t('menu.expandAll')}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        setExpandedCategories(new Set());
                      }}
                      className="text-xs flex-1 sm:flex-none"
                    >
                      {t('menu.collapseAll')}
                    </Button>
                  </div>
                  <Badge variant="outline" className="text-sm sm:text-base px-3 py-1 text-center">
                    {extractedCategories.filter((c) => c.selected).length} {t('menu.categories')},{' '}
                    {extractedItems.filter((i) => i.selected).length} {t('menu.items')}
                  </Badge>
                </div>
              </div>

              {extractedCategories.length === 0 && extractedItems.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-muted-foreground">
                    {t('menu.noItemsDetected')}
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-6">
                  {extractedCategories.map((category) => {
                    const isExpanded = expandedCategories.has(category.id);
                    const categoryItems = extractedItems.filter((item) => item.categoryId === category.id);
                    const toggleExpand = () => {
                      const newExpanded = new Set(expandedCategories);
                      if (isExpanded) {
                        newExpanded.delete(category.id);
                      } else {
                        newExpanded.add(category.id);
                      }
                      setExpandedCategories(newExpanded);
                    };

                    return (
                      <Card key={category.id} className="shadow-sm">
                        <CardHeader 
                          className="bg-muted/30 pb-4 cursor-pointer hover:bg-muted/40 transition-colors overflow-hidden"
                          onClick={toggleExpand}
                        >
                          <div className="flex items-start gap-2 sm:gap-3 min-w-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCategory(category.id);
                              }}
                              className="mt-1 flex-shrink-0"
                            >
                              <Checkbox
                                checked={category.selected}
                                onCheckedChange={() => toggleCategory(category.id)}
                                className="pointer-events-none"
                              />
                            </button>
                            <div className="flex-1 space-y-3 min-w-0 overflow-hidden">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <div className="flex items-center gap-2 flex-wrap min-w-0">
                                  {isExpanded ? (
                                    <ChevronDown className="w-5 h-5 text-foreground flex-shrink-0 font-semibold" />
                                  ) : (
                                    <ChevronRight className="w-5 h-5 text-foreground flex-shrink-0 font-semibold" />
                                  )}
                                  <Badge variant="secondary" className="text-sm flex-shrink-0">
                                    {t('menu.category')}
                                  </Badge>
                                  {categoryItems.length > 0 && (
                                    <Badge variant="outline" className="text-xs flex-shrink-0">
                                      {categoryItems.length} {t('menu.items')}
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => addNewItemToCategory(category.id)}
                                    className="gap-1 h-7 text-xs"
                                  >
                                    <Plus className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">{t('menu.addItem')}</span>
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => deleteCategory(category.id)}
                                    className="h-7 w-7 flex-shrink-0"
                                  >
                                    <Trash2 className="w-3 h-3 text-destructive" />
                                  </Button>
                                </div>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0" onClick={(e) => e.stopPropagation()}>
                                <div className="min-w-0">
                                  <Label className="text-xs text-muted-foreground mb-1">
                                    {i18n.language === 'ar' ? 'العربية' : 'Arabe'}
                                  </Label>
                                  <Input
                                    value={category.nameAr}
                                    onChange={(e) =>
                                      updateCategory(category.id, 'nameAr', e.target.value)
                                    }
                                    className="font-medium w-full"
                                    placeholder="اسم الفئة"
                                  />
                                </div>
                                <div className="min-w-0">
                                  <Label className="text-xs text-muted-foreground mb-1">
                                    {i18n.language === 'ar' ? 'Français' : 'Français'}
                                  </Label>
                                  <Input
                                    value={category.nameFr}
                                    onChange={(e) =>
                                      updateCategory(category.id, 'nameFr', e.target.value)
                                    }
                                    className="font-medium w-full"
                                    placeholder="Nom de catégorie"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        {isExpanded && (
                          <CardContent className="space-y-3 pt-4">
                        {extractedItems
                          .filter((item) => item.categoryId === category.id)
                          .map((item) => (
                            <div
                              key={item.id}
                              className="flex items-start gap-3 p-3 border rounded-lg bg-card hover:bg-accent/50 transition-colors"
                            >
                              <Checkbox
                                checked={item.selected}
                                onCheckedChange={() => toggleItem(item.id)}
                                className="mt-1"
                              />
                              <div className="flex-1 space-y-3">
                                {/* Category selector for moving items */}
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 pb-2 border-b">
                                  <div className="flex items-center gap-2">
                                    <MoveHorizontal className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                    <Label className="text-xs text-muted-foreground">
                                      {t('menu.category')}:
                                    </Label>
                                  </div>
                                  <Select
                                    value={item.categoryId}
                                    onValueChange={(value) => moveItemToCategory(item.id, value)}
                                  >
                                    <SelectTrigger className="h-8 w-full sm:w-[200px]">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {extractedCategories.map((cat) => (
                                        <SelectItem key={cat.id} value={cat.id}>
                                          {i18n.language === 'ar'
                                            ? cat.nameAr || cat.nameFr
                                            : cat.nameFr || cat.nameAr}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-[1fr,auto] gap-3">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs text-muted-foreground mb-1">
                                        {i18n.language === 'ar' ? 'العربية' : 'Arabe'}
                                      </Label>
                                      <Input
                                        value={item.nameAr}
                                        onChange={(e) =>
                                          updateItem(item.id, 'nameAr', e.target.value)
                                        }
                                        placeholder="اسم الطبق"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs text-muted-foreground mb-1">
                                        Français
                                      </Label>
                                      <Input
                                        value={item.nameFr}
                                        onChange={(e) =>
                                          updateItem(item.id, 'nameFr', e.target.value)
                                        }
                                        placeholder="Nom du plat"
                                      />
                                    </div>
                                  </div>
                                  <div className="w-full sm:w-32">
                                    <Label className="text-xs text-muted-foreground mb-1">
                                      {t('menu.price')}
                                    </Label>
                                    <div className="flex items-center gap-2">
                                      <Input
                                        type="number"
                                        value={item.price === 0 ? '' : item.price}
                                        onChange={(e) => {
                                          const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                          updateItem(item.id, 'price', isNaN(value) ? 0 : value);
                                        }}
                                        className="w-full sm:w-20"
                                        step="0.01"
                                        placeholder="0.00"
                                      />
                                      <span className="text-sm font-medium flex-shrink-0">DH</span>
                                    </div>
                                  </div>
                                </div>
                                {/* Always show description fields */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <div>
                                    <Label className="text-xs text-muted-foreground mb-1">
                                      {t('menu.description')} (العربية)
                                    </Label>
                                    <Input
                                      value={item.descriptionAr}
                                      onChange={(e) =>
                                        updateItem(item.id, 'descriptionAr', e.target.value)
                                      }
                                      className="text-sm"
                                      placeholder="الوصف"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-muted-foreground mb-1">
                                      {t('menu.description')} (Français)
                                    </Label>
                                    <Input
                                      value={item.descriptionFr}
                                      onChange={(e) =>
                                        updateItem(item.id, 'descriptionFr', e.target.value)
                                      }
                                      className="text-sm"
                                      placeholder="Description"
                                    />
                                  </div>
                                </div>

                                {/* Specialties Selection */}
                                {specialties && specialties.length > 0 && (
                                  <div className="space-y-2 pt-2 border-t">
                                    <Label className="text-xs text-muted-foreground">
                                      {t('menu.specialties')}
                                    </Label>
                                    <div className="flex flex-wrap gap-2">
                                      {specialties.map((specialty) => (
                                        <div key={specialty.id} className="flex items-center gap-1">
                                          <Checkbox
                                            checked={
                                              item.specialtyIds?.includes(specialty.id) || false
                                            }
                                            onCheckedChange={(checked) => {
                                              const currentIds = item.specialtyIds || [];
                                              const newIds = checked
                                                ? [...currentIds, specialty.id]
                                                : currentIds.filter((id) => id !== specialty.id);
                                              updateItem(item.id, 'specialtyIds', newIds);
                                            }}
                                          />
                                          <Badge
                                            style={{ backgroundColor: specialty.color }}
                                            className="text-white text-xs cursor-pointer"
                                            onClick={() => {
                                              const currentIds = item.specialtyIds || [];
                                              const isSelected = currentIds.includes(specialty.id);
                                              const newIds = isSelected
                                                ? currentIds.filter((id) => id !== specialty.id)
                                                : [...currentIds, specialty.id];
                                              updateItem(item.id, 'specialtyIds', newIds);
                                            }}
                                          >
                                            {i18n.language === 'ar'
                                              ? specialty.nameAr
                                              : specialty.nameFr}
                                          </Badge>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Ingredients Selection */}
                                {ingredients && ingredients.length > 0 && (
                                  <div className="space-y-2 pt-2 border-t">
                                    <Label className="text-xs text-muted-foreground">
                                      {t('menu.ingredients')}
                                    </Label>

                                    {/* Add Ingredient Form */}
                                    <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr_auto] gap-2">
                                      <Select
                                        value={ingredientForm[item.id]?.ingredientId || ''}
                                        onValueChange={(ingredientId) => {
                                          const inventoryItem = inventory?.find(
                                            (inv) => inv.ingredientId === ingredientId
                                          );
                                          const unit = inventoryItem?.unit || 'unit';
                                          setIngredientForm((prev) => ({
                                            ...prev,
                                            [item.id]: {
                                              ingredientId,
                                              quantity: prev[item.id]?.quantity || 1,
                                              unit: unit as MeasurementUnit,
                                            },
                                          }));
                                        }}
                                      >
                                        <SelectTrigger className="h-8 text-xs">
                                          <SelectValue placeholder={t('menu.selectIngredient')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {ingredients.map((ingredient) => (
                                            <SelectItem key={ingredient.id} value={ingredient.id}>
                                              <div className="flex items-center gap-2">
                                                <span>
                                                  {i18n.language === 'ar'
                                                    ? ingredient.nameAr
                                                    : ingredient.nameFr}
                                                </span>
                                              </div>
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>

                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                        value={ingredientForm[item.id]?.quantity === 0 ? '' : ingredientForm[item.id]?.quantity || ''}
                                        onChange={(e) => {
                                          const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                          setIngredientForm((prev) => ({
                                            ...prev,
                                            [item.id]: {
                                              ...prev[item.id],
                                              ingredientId: prev[item.id]?.ingredientId || '',
                                              quantity: isNaN(value) ? 0 : value,
                                              unit: prev[item.id]?.unit || 'unit',
                                            },
                                          }));
                                        }}
                                        className="h-8 text-xs"
                                      />

                                      <Select
                                        value={ingredientForm[item.id]?.unit || 'unit'}
                                        onValueChange={(unit) => {
                                          setIngredientForm((prev) => ({
                                            ...prev,
                                            [item.id]: {
                                              ...prev[item.id],
                                              ingredientId: prev[item.id]?.ingredientId || '',
                                              quantity: prev[item.id]?.quantity || 1,
                                              unit: unit as MeasurementUnit,
                                            },
                                          }));
                                        }}
                                      >
                                        <SelectTrigger className="h-8 text-xs">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {(() => {
                                            const selectedIngredientId =
                                              ingredientForm[item.id]?.ingredientId;
                                            const inventoryItem = inventory?.find(
                                              (inv) => inv.ingredientId === selectedIngredientId
                                            );
                                            const availableUnits = inventoryItem
                                              ? getCompatibleUnits(inventoryItem.unit)
                                              : ['g', 'kg', 'ml', 'cl', 'l', 'unit'];
                                            return availableUnits.map((unit) => (
                                              <SelectItem key={unit} value={unit}>
                                                {unit}
                                              </SelectItem>
                                            ));
                                          })()}
                                        </SelectContent>
                                      </Select>

                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        className="h-8"
                                        onClick={() => {
                                          const form = ingredientForm[item.id];
                                          if (!form?.ingredientId || form.quantity <= 0) return;

                                          const currentIngredients = item.ingredients || [];
                                          const alreadyAdded = currentIngredients.some(
                                            (ing) => ing.ingredientId === form.ingredientId
                                          );

                                          if (alreadyAdded) {
                                            toast({
                                              title: t('common.error'),
                                              description: t('menu.ingredientAlreadyAdded'),
                                              variant: 'destructive',
                                            });
                                            return;
                                          }

                                          const newIngredient: MenuItemIngredientInput = {
                                            ingredientId: form.ingredientId,
                                            quantity: form.quantity,
                                            unit: form.unit,
                                            menuItemOptionId: null,
                                          };

                                          updateItem(item.id, 'ingredients', [
                                            ...currentIngredients,
                                            newIngredient,
                                          ]);

                                          // Clear form
                                          setIngredientForm((prev) => {
                                            const newForm = { ...prev };
                                            delete newForm[item.id];
                                            return newForm;
                                          });
                                        }}
                                      >
                                        <Plus className="w-3 h-3" />
                                      </Button>
                                    </div>

                                    {/* Ingredients List */}
                                    {item.ingredients && item.ingredients.length > 0 && (
                                      <div className="space-y-1">
                                        {item.ingredients.map((ing, index) => {
                                          const ingredient = ingredients?.find(
                                            (i) => i.id === ing.ingredientId
                                          );
                                          return (
                                            <div
                                              key={`${ing.ingredientId}-${index}`}
                                              className="flex items-center justify-between p-2 bg-muted rounded text-xs"
                                            >
                                              <div className="flex items-center gap-2">
                                                <IngredientIcon
                                                  ingredient={ingredient || null}
                                                  size={16}
                                                />
                                                <span>
                                                  {ingredient
                                                    ? i18n.language === 'ar'
                                                      ? ingredient.nameAr
                                                      : ingredient.nameFr
                                                    : 'Unknown'}
                                                </span>
                                                <span className="text-muted-foreground">
                                                  {ing.quantity} {ing.unit}
                                                </span>
                                              </div>
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                onClick={() => {
                                                  const newIngredients = item.ingredients.filter(
                                                    (_, i) => i !== index
                                                  );
                                                  updateItem(
                                                    item.id,
                                                    'ingredients',
                                                    newIngredients
                                                  );
                                                }}
                                              >
                                                <Trash2 className="w-3 h-3 text-destructive" />
                                              </Button>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 shrink-0"
                                onClick={() => deleteItem(item.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          ))}
                        </CardContent>
                      )}
                    </Card>
                  );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Importing Step */}
          {currentStep === 'importing' && (
            <div className="space-y-4 p-4">
              <div className="text-center space-y-4">
                <div className="inline-block p-4 rounded-full bg-primary/10">
                  <Upload className="w-12 h-12 text-primary animate-pulse" />
                </div>
                <div>
                  <p className="text-lg font-medium">{t('menu.importing')}</p>
                  <p className="text-sm text-muted-foreground">{t('menu.pleaseWait')}</p>
                </div>
                <Progress value={progress} className="w-full max-w-md mx-auto" />
                <p className="text-sm text-muted-foreground">{progress}%</p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={handleClose} disabled={isProcessing}>
            {t('common.cancel')}
          </Button>
          {currentStep === 'upload' && (
            <Button onClick={processImage} disabled={!image || isProcessing}>
              {t('menu.processImage')}
            </Button>
          )}
          {currentStep === 'review' && (
            <Button
              onClick={handleImport}
              disabled={
                extractedCategories.filter((c) => c.selected).length === 0 &&
                extractedItems.filter((i) => i.selected).length === 0
              }
            >
              {t('menu.importSelected')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
